import classNames from 'classnames';
import {Button, ButtonProps, Dropdown, Menu} from 'antd';

import useCommon from '@utils/hooks/common';
import styles from './styles';
import {useEffect, useState} from 'react';
import {autorun, toJS} from 'mobx';
import TabBubble from '@components/game-room/TabBubble';
import {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';
import {TabBubbleUiInterface} from '@interface/index';
import {useRouter} from 'next/router';
import {
  CloseCircleOutlined,
  LoadingOutlined,
  PauseCircleOutlined,
  PlayCircleFilled,
} from '@ant-design/icons';
import React from 'react';
import {AnimatePresence, motion} from 'framer-motion';

interface TabBubbleSectionInGameProps {
  className?: string;
  isSitOutNextHand: boolean[];
  handleMenuClick: (e, index: number) => void;
  isQuittingTable: boolean;
  isConfirmLeavingPopUpVisible?: boolean;
  observerStatus?: boolean[];
}

enum DropdownItems {
  LEAVE_GAME,
  LEAVE_SEAT,
  SIT_IN,
}

const TabBubbleSectionInGame = (props: TabBubbleSectionInGameProps): JSX.Element[] => {
  const {classes, t, language, user, system} = useCommon({
    withTranslation: ['game_room'],
    withStyles: styles,
    observers: ['user', 'language', 'system'],
  });
  const router = useRouter();
  const isDebug = router?.query?.isDebug === 'true';

  const {
    className,
    children,
    isSitOutNextHand,
    handleMenuClick,
    isQuittingTable,
    isConfirmLeavingPopUpVisible,
    observerStatus,
  } = props;

  const [joinedRooms, setJoinedRooms] = useState<Array<JoinedRoomProps>>([]);
  const [bubbleContent, setButtonContent] = useState<TabBubbleUiInterface[]>([
    null,
    null,
    null,
    null,
  ]);
  const [visibleDropDownIndex, setVisibleDropDownIndex] = useState<number>(null);
  const [activeTableIndex, setActiveTableIndex] = useState<number>(0);

  useEffect(() => {
    autorun(() => {
      if (user?.joinedRooms?.length > 0) {
        if (JSON.stringify(user?.joinedRooms) != JSON.stringify(joinedRooms)) {
          setJoinedRooms(user?.joinedRooms || []);
        }
      }

      if (JSON.stringify(system?.tabBubbleContent) != JSON.stringify(bubbleContent)) {
        setButtonContent(system?.tabBubbleContent.map(element => element));
      }

      setActiveTableIndex(system?.activeTableIndex || 0);
    });
  });

  useEffect(() => {
    if (isConfirmLeavingPopUpVisible) {
      setVisibleDropDownIndex(null);
    }
  }, [isConfirmLeavingPopUpVisible]);

  const getLeaveSeatMenu = (tableIndex: number) => {
    const currentTable = joinedRooms[tableIndex];
    const isTournament = currentTable?.roomType === 'TOURNAMENT';
    
    return (
      <Menu
        className={classes.leaveGameMenu}
        onClick={e => {
          handleMenuClick(e, tableIndex);
          if (
            e.key === DropdownItems.LEAVE_SEAT.toString() ||
            e.key === DropdownItems.SIT_IN.toString()
          ) {
            console.log(' @@ closing dropdown in 1000ms');
            setTimeout(() => {
              setVisibleDropDownIndex(null);
            }, 1000);
          }
        }}
      >
        <Menu.Item
          className={classes.leaveGameMenuItem}
          key={DropdownItems.LEAVE_GAME.toString()}
          icon={isQuittingTable ? <LoadingOutlined /> : <CloseCircleOutlined />}
        >
          {isTournament ? t('back_to_lobby') : t('leave_game')}
        </Menu.Item>
      {isSitOutNextHand[tableIndex] ? (
        <Menu.Item
          className={classNames(classes.leaveTableMenuItem, {
            [classes.hidden]: observerStatus[tableIndex],
          })}
          key={DropdownItems.SIT_IN.toString()}
          icon={<PlayCircleFilled />}
        >
          {t('sit_in')}
        </Menu.Item>
      ) : (
        <Menu.Item
          className={classNames(classes.leaveTableMenuItem, {
            [classes.hidden]: observerStatus[tableIndex],
          })}
          key={DropdownItems.LEAVE_SEAT.toString()}
          icon={<PauseCircleOutlined />}
        >
          {t('leave_seat')}
        </Menu.Item>
      )}
    </Menu>
    );
  };

  // console.log(' visibleDropDownIndex, activeTableIndex', visibleDropDownIndex, activeTableIndex);

  return joinedRooms?.map((info, tableIndex) => (
    <div key={info?.roomCode} className={classes.buttonContainer}>
      <AnimatePresence>
        {visibleDropDownIndex === tableIndex && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className={classes.dropdownMask}
            onClick={e => {
              e.preventDefault();
              setVisibleDropDownIndex(null);
            }}
          />
        )}
      </AnimatePresence>

      <Dropdown
        overlay={() => getLeaveSeatMenu(tableIndex)}
        placement="bottom"
        open={visibleDropDownIndex === tableIndex}
        // trigger={['click']}
      >
        <TabBubble
          index={tableIndex}
          roomCode={info?.roomCode}
          bubbleData={bubbleContent[tableIndex]}
          isHighlighted={tableIndex === activeTableIndex}
          // * hide onHover, focus on mobile first
          // onHover={() => {
          //   if (tableIndex === activeTableIndex) {
          //     setVisibleDropDownIndex(tableIndex);
          //   }
          // }}
          onClick={() => {
            if (tableIndex === activeTableIndex && visibleDropDownIndex !== tableIndex) {
              setVisibleDropDownIndex(tableIndex);
            } else if (tableIndex === activeTableIndex && visibleDropDownIndex === tableIndex) {
              setVisibleDropDownIndex(null);
            }

            // avoid next/router re-rendering
            const newQuery = new URLSearchParams({
              ...router.query,
              currentRoom: info?.roomCode,
            }).toString();
            const newUrl = `${router.pathname}?${newQuery}`;

            window.history.replaceState({}, '', newUrl);

            system.setActiveTableIndex(tableIndex);
          }}
        />
      </Dropdown>
    </div>
  ));
};

export default React.memo(TabBubbleSectionInGame);
